import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Meh, CloudRain, Home, ArrowRight, Sparkles, Wind, RefreshCw, MessageCircle } from 'lucide-react';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';
import { PrimaryButton, SecondaryButton } from './brand/BrandButton';

/**
 * SessionCompletion - Post-tool reflection with smart suggestions
 * 
 * Shows after completing a regulation tool:
 * 1. Asks "How do you feel now?"
 * 2. Based on answer, suggests next action
 * 
 * Reflection options:
 * - Better (green) → Positive reinforcement + return home
 * - About the same (neutral) → Suggest related tool
 * - Need more support (gentle) → Offer deeper support options
 */

// Tool suggestions based on completion context
const TOOL_SUGGESTIONS = {
    breathing: [
        { path: '/regulate/breathing-orb', title: 'Calm Cycle', description: 'A steady rhythm to settle your system', icon: Heart },
        { path: '/regulate/micro-reset', title: 'Micro Reset', description: 'Quick 60-second reset', icon: RefreshCw },
    ],
    grounding: [
        { path: '/regulate/box-breathing', title: 'Box Breathing', description: 'Calming breathing exercise', icon: Wind },
        { path: '/regulate/self-talk', title: 'Self-Talk Reframes', description: 'Gentle words to shift narratives', icon: MessageCircle },
    ],
    emotional: [
        { path: '/regulate/regulation-navigator', title: 'Regulation Navigator', description: 'Find the right tool for how you feel', icon: Sparkles },
        { path: '/regulate/box-breathing', title: 'Box Breathing', description: 'Calming breathing exercise', icon: Wind },
    ],
    relationship: [
        { path: '/coregulate', title: 'Co-Regulation', description: 'Regulate together with your partner', icon: Heart },
        { path: '/communicate', title: 'Communication Scripts', description: 'Words for difficult conversations', icon: MessageCircle },
    ],
    default: [
        { path: '/regulate/box-breathing', title: 'Box Breathing', description: 'Calming breathing exercise', icon: Wind },
        { path: '/regulate/regulation-navigator', title: 'Regulation Navigator', description: 'Find the right tool', icon: Sparkles },
    ]
};

const REFLECTION_OPTIONS = [
    {
        id: 'better',
        label: 'Better',
        description: 'I feel more settled',
        icon: Heart,
        color: 'text-[#1D6B6B]',
        bgColor: 'bg-[#7D8E95]/20',
        borderColor: 'border-[#7D8E95]',
        hoverBg: 'hover:bg-[#7D8E95]/30'
    },
    {
        id: 'same',
        label: 'About the same',
        description: 'Still processing',
        icon: Meh,
        color: 'text-[#FFBB98]',
        bgColor: 'bg-[#FFBB98]/20',
        borderColor: 'border-[#FFBB98]',
        hoverBg: 'hover:bg-[#FFBB98]/30'
    },
    {
        id: 'need-more',
        label: 'Need more support',
        description: 'I\'m still struggling',
        icon: CloudRain,
        color: 'text-[#7D8E95]',
        bgColor: 'bg-[#7D8E95]/20',
        borderColor: 'border-[#7D8E95]',
        hoverBg: 'hover:bg-[#7D8E95]/30'
    }
];

export const SessionCompletion = ({ 
    toolName = 'this exercise',
    toolCategory = 'default', // breathing, grounding, emotional, relationship
    onClose,
    onContinue 
}) => {
    const navigate = useNavigate();
    const [selectedReflection, setSelectedReflection] = useState(null);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const handleReflectionSelect = (reflectionId) => {
        setSelectedReflection(reflectionId);
        
        // Small delay before showing next step
        setTimeout(() => {
            setShowSuggestions(true);
        }, 300);
    };

    const handleGoHome = () => {
        navigate('/home');
        onClose?.();
    };

    const handleTrySuggestion = (path) => {
        navigate(path);
        onClose?.();
    };

    const suggestions = TOOL_SUGGESTIONS[toolCategory] || TOOL_SUGGESTIONS.default;

    // Render based on state
    if (!showSuggestions) {
        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-[#FEF6EF] rounded-3xl max-w-md w-full p-8 text-center animate-in zoom-in-95 duration-300">
                    {/* Completion Icon */}
                    <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-6">
                        <Sparkles className="w-8 h-8 text-[#1D6B6B]" />
                    </div>

                    {/* Message */}
                    <Heading as="h2" size="text-xl" className="mb-2">
                        Well done
                    </Heading>
                    <BodyText className="text-[#7D8E95] mb-8">
                        You completed {toolName}. Take a moment to notice how you feel.
                    </BodyText>

                    {/* Reflection Question */}
                    <SmallText className="text-[#7D8E95] font-medium mb-4">
                        How do you feel now?
                    </SmallText>

                    {/* Reflection Options */}
                    <div className="space-y-3">
                        {REFLECTION_OPTIONS.map(option => {
                            const Icon = option.icon;
                            const isSelected = selectedReflection === option.id;
                            
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => handleReflectionSelect(option.id)}
                                    className={`w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-center gap-4 ${
                                        isSelected 
                                            ? `${option.bgColor} ${option.borderColor}` 
                                            : `bg-white border-[#FFBB98] ${option.hoverBg}`
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-full ${option.bgColor} flex items-center justify-center`}>
                                        <Icon className={`w-5 h-5 ${option.color}`} />
                                    </div>
                                    <div className="text-left flex-1">
                                        <p className={`font-medium ${isSelected ? option.color : 'text-[#1D6B6B]'}`}>
                                            {option.label}
                                        </p>
                                        <SmallText className="text-[#7D8E95]">{option.description}</SmallText>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // Show suggestions based on reflection
    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#FEF6EF] rounded-3xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300">
                {selectedReflection === 'better' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                            <Heading as="h2" size="text-xl" className="mb-2">
                                That's wonderful
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Your nervous system is finding its way back. This feeling is available to you anytime.
                            </BodyText>
                        </div>
                        
                        <div className="space-y-3">
                            <PrimaryButton onClick={handleGoHome} className="w-full">
                                <Home className="w-4 h-4 mr-2" />
                                Return to Dashboard
                            </PrimaryButton>
                            <SecondaryButton onClick={() => setShowSuggestions(false)} className="w-full">
                                Try another tool
                            </SecondaryButton>
                        </div>
                    </>
                )}

                {selectedReflection === 'same' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-4">
                                <Meh className="w-8 h-8 text-[#FFBB98]" />
                            </div>
                            <Heading as="h2" size="text-xl" className="mb-2">
                                That's okay
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Sometimes regulation takes a few tries. Would you like to try something different?
                            </BodyText>
                        </div>
                        
                        <SmallText className="text-[#7D8E95] font-medium mb-3">
                            Suggested next steps
                        </SmallText>
                        
                        <div className="space-y-2 mb-4">
                            {suggestions.map((tool, index) => {
                                const ToolIcon = tool.icon;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => handleTrySuggestion(tool.path)}
                                        className="w-full p-3 rounded-xl bg-white border border-[#FFBB98] hover:border-[#7D8E95] transition-all flex items-center gap-3 text-left"
                                    >
                                        <div className="w-10 h-10 rounded-full bg-[#7D8E95]/10 flex items-center justify-center">
                                            <ToolIcon className="w-5 h-5 text-[#1D6B6B]" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-[#1D6B6B] text-sm">{tool.title}</p>
                                            <SmallText className="text-[#7D8E95]">{tool.description}</SmallText>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-[#7D8E95]" />
                                    </button>
                                );
                            })}
                        </div>
                        
                        <SecondaryButton onClick={handleGoHome} className="w-full">
                            Return to Dashboard
                        </SecondaryButton>
                    </>
                )}

                {selectedReflection === 'need-more' && (
                    <>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-4">
                                <CloudRain className="w-8 h-8 text-[#7D8E95]" />
                            </div>
                            <Heading as="h2" size="text-xl" className="mb-2">
                                You're not alone
                            </Heading>
                            <BodyText className="text-[#7D8E95]">
                                Some moments need more support—that's completely valid. Here are some options that might help.
                            </BodyText>
                        </div>
                        
                        <SmallText className="text-[#7D8E95] font-medium mb-3">
                            Deeper support options
                        </SmallText>
                        
                        <div className="space-y-2 mb-4">
                            <button
                                onClick={() => handleTrySuggestion('/regulate/regulation-navigator')}
                                className="w-full p-3 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/30 hover:border-[#7D8E95] transition-all flex items-center gap-3 text-left"
                            >
                                <div className="w-10 h-10 rounded-full bg-[#7D8E95]/20 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-[#7D8E95]" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-[#1D6B6B] text-sm">Find what you need</p>
                                    <SmallText className="text-[#7D8E95]">Let me guide you to the right tool</SmallText>
                                </div>
                                <ArrowRight className="w-4 h-4 text-[#7D8E95]" />
                            </button>
                            
                            <button
                                onClick={() => handleTrySuggestion('/coregulate')}
                                className="w-full p-3 rounded-xl bg-[#FFBB98]/10 border border-[#FFBB98]/30 hover:border-[#FFBB98] transition-all flex items-center gap-3 text-left"
                            >
                                <div className="w-10 h-10 rounded-full bg-[#FFBB98]/20 flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-[#FFBB98]" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-[#1D6B6B] text-sm">Co-Regulate</p>
                                    <SmallText className="text-[#7D8E95]">Regulate with someone you trust</SmallText>
                                </div>
                                <ArrowRight className="w-4 h-4 text-[#7D8E95]" />
                            </button>
                            
                            {suggestions[0] && (
                                <button
                                    onClick={() => handleTrySuggestion(suggestions[0].path)}
                                    className="w-full p-3 rounded-xl bg-white border border-[#FFBB98] hover:border-[#7D8E95] transition-all flex items-center gap-3 text-left"
                                >
                                    <div className="w-10 h-10 rounded-full bg-[#7D8E95]/10 flex items-center justify-center">
                                        {React.createElement(suggestions[0].icon, { className: "w-5 h-5 text-[#1D6B6B]" })}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-[#1D6B6B] text-sm">{suggestions[0].title}</p>
                                        <SmallText className="text-[#7D8E95]">{suggestions[0].description}</SmallText>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-[#7D8E95]" />
                                </button>
                            )}
                        </div>
                        
                        <SecondaryButton onClick={handleGoHome} className="w-full">
                            Take a break and return later
                        </SecondaryButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default SessionCompletion;
